/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useEffect, useRef, useState} from "react";
import classes from "@/app/components/algolia/SearchBox/searchBoxStyles.module.scss";
import {useInstantSearch, useSearchBox} from "react-instantsearch";
import SearchSvg from "@/app/components/assets/common/SearchSvg";
import CloseButton from "@/app/components/assets/common/CloseButton";
import {Button} from "@mui/material";
import {
    DELAY_BEFORE_SENDING_REQUEST_FOR_SEARCH_FIELD_KEYDOWN,
    SEARCH_MIN_QUERY_LENGTH
} from "@/app/config/algoliaConfig";
import {useAppSelector} from "@/app/hooks/redux";

const SearchBox = (props:{
    defaultText:string,
    placeholder?:string,
    className?:string,
    autofocus?: boolean,
    attributes?:any,
}) => {
    const {storeConfig:storeConfigData} = useAppSelector(state => state.storeConfigReducer);
    const algoliaSearchKeyDownTimer = useRef<NodeJS.Timeout | null>(null);
    const { refine, query } = useSearchBox();
    const defaultText = query || props.defaultText || '';
    const [inputValue, setInputValue] = useState(defaultText);
    const { status } = useInstantSearch();
    const inputRef = useRef<any>(null);
    const isSearchStalled = status === 'stalled';
    const autofocus = props.autofocus ?? false;
    const minQueryLength = storeConfigData?.algolia_autocomplete_min_query_length
        ? parseInt(storeConfigData?.algolia_autocomplete_min_query_length)
        : SEARCH_MIN_QUERY_LENGTH;
    const searchQueryDebounceTime = storeConfigData?.algolia_autocomplete_debounce_time
        ? parseInt(storeConfigData?.algolia_autocomplete_debounce_time)
        : DELAY_BEFORE_SENDING_REQUEST_FOR_SEARCH_FIELD_KEYDOWN;

    useEffect(() => {
        if (defaultText !== query) {
            if (query) {
                setInputValue(defaultText);
                refine(defaultText);
            }
            else {
                setInputValue('');
                refine('');
            }
        }
    }, [defaultText, query]);

    useEffect(() => {
        if (query !== inputValue) {
            setInputValue(query || '');
        }
    }, []);

    function setQuery(newQuery:string) {
        setInputValue(newQuery);

        // Clear the existing timer if it's already set
        if (algoliaSearchKeyDownTimer.current) {
            clearTimeout(algoliaSearchKeyDownTimer.current);
        }

        if(newQuery.length >= minQueryLength) {
            // Set a new timer for 1 second
            algoliaSearchKeyDownTimer.current = setTimeout(() => {
                refine(newQuery);
            }, searchQueryDebounceTime);
        }
    }

    return (
        <div className={`${classes.wrapper} ${props.className ?? ''}`}>
            <form
                action=""
                role="search"
                noValidate
                className={`${classes.form}`}
                onSubmit={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (inputRef.current) {
                        inputRef.current.blur();
                    }
                }}
                onReset={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setQuery('');
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                }}
            >
                <input
                    ref={inputRef}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    aria-label={props.placeholder ?? 'Search'}
                    placeholder={props.placeholder ?? ''}
                    spellCheck={false}
                    maxLength={128}
                    type="text"
                    value={inputValue}
                    onChange={(event) => {
                        setQuery(event.currentTarget.value);
                    }}
                    className={`${classes.searchField}`}
                    autoFocus={autofocus}
                />
                <Button type="submit" variant={'text'} className={`${classes.searchSubmit}`} aria-label={'Search'}><SearchSvg /></Button>
                {!(inputValue.length === 0 || isSearchStalled) && <Button
                    type="reset"
                    variant={'text'}
                    className={`${classes.searchReset}`}
                >
                    <CloseButton />
                </Button>}
            </form>
        </div>
    );
}

export default SearchBox;